import { Flex } from "antd";
import { useEffect, useState } from "react";
import { shallowEqual } from "react-redux";

import { useAppSelector, useProtectedUserRoleSelector } from "@/hooks/redux";
import { PointCardSelectable } from "@/pages/SV/RouteManagement/AddPoint/components/PointCardSelectable";
import { PointsSearchInput } from "@/pages/SV/RouteManagement/AddPoint/components/PointsSearchInput";
import { RegionPicker } from "@/pages/SV/RouteManagement/AddPoint/components/RegionPicker";
import { usePointsWithoutRoute } from "@/pages/SV/RouteManagement/AddPoint/hooks";
import { BlockLoader } from "@/shared/ui/BlockLoader";

interface AddPointListBlockProps {
    bootLoading: boolean;
    checkedPoints: Set<string>;
    onTogglePointChecked: (pointId: string) => void;
}

export const AddPointListBlock = ({
    bootLoading,
    checkedPoints,
    onTogglePointChecked,
}: AddPointListBlockProps) => {
    const role = useProtectedUserRoleSelector();

    const { regions, employee } = useAppSelector(
        (s) => ({
            regions: s.user.regions,
            employee: s.user.employee,
        }),
        shallowEqual,
    );

    const currentScheduleList = useAppSelector(
        (state) => state.manager.currentScheduleList,
    );

    const [selectedAreaId, setSelectedAreaId] = useState<string>("");

    useEffect(() => {
        if (employee?.context?.areaId && !selectedAreaId) {
            setSelectedAreaId(employee?.context?.areaId);
        }
    }, [employee?.context?.areaId, selectedAreaId]);

    const {
        points: pointsWithoutRoute,
        loading: pointsLoading,
        refetch: fetchPoints,
    } = usePointsWithoutRoute({
        areaId: selectedAreaId,
        partnerId: employee?.context?.clientId,
        role,
    });

    const displayPointsWithoutRoute = pointsWithoutRoute.filter(
        ({ id }) => !currentScheduleList.some(({ pointId }) => pointId === id),
    );

    const handleRegionChange = (value: string) => {
        setSelectedAreaId(value);
    };

    const loading = bootLoading || pointsLoading;

    return (
        <>
            <RegionPicker
                regions={regions}
                value={selectedAreaId}
                onChange={handleRegionChange}
            />

            <PointsSearchInput loading={loading} fetchPoints={fetchPoints} />

            {loading ? (
                <Flex flex={1} justify="center">
                    <BlockLoader />
                </Flex>
            ) : (
                <Flex vertical gap={16}>
                    {displayPointsWithoutRoute.map((item) => {
                        const checked = checkedPoints.has(item.id);

                        return (
                            <PointCardSelectable
                                checked={checked}
                                toggleChecked={() =>
                                    onTogglePointChecked(item.id)
                                }
                                point={item}
                                key={item.id}
                            />
                        );
                    })}
                </Flex>
            )}
        </>
    );
};
